// src/components/dev/utils/uploader.ts

// Type Definitions
interface UploadTaskSnapshot {
  bytesTransferred: number;
  totalBytes: number;
  state: string;
  ref: StorageReference;
}

interface StorageReference {
  put: (file: File) => UploadTask;
  getDownloadURL: () => Promise<string>;
  delete: () => Promise<void>;
  fullPath: string;
}

interface UploadTask {
  snapshot: UploadTaskSnapshot;
  on: (
    event: string,
    next: (snapshot: UploadTaskSnapshot) => void,
    error: (error: StorageError) => void,
    complete: () => Promise<void>
  ) => void;
}

interface StorageError {
  code: string;
  message?: string;
}

interface TaskEvent {
  STATE_CHANGED: string;
}

interface TaskState {
  PAUSED: string;
  RUNNING: string;
}

// Helper Functions
export const uploadFile = (
  ref: StorageReference,
  file: File,
  { TaskEvent, TaskState }: { TaskEvent: TaskEvent; TaskState: TaskState }
): Promise<string> => {
  const uploadTask = ref.put(file);
  return new Promise((resolve, reject) => {
    uploadTask.on(
      TaskEvent.STATE_CHANGED,
      (snapshot: UploadTaskSnapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload is ${progress}% done`);
        switch (snapshot.state) {
          case TaskState.PAUSED:
            console.log("Upload is paused");
            break;
          case TaskState.RUNNING:
            console.log("Upload is running");
            break;
          default:
            console.log(`Unknown upload state: ${snapshot.state}`);
        }
      },
      (error: StorageError) => {
        switch (error.code) {
          case "storage/unauthorized":
            console.error("Upload failed: Unauthorized access", error);
            break;
          case "storage/canceled":
            console.error("Upload failed: Canceled", error);
            break;
          case "storage/unknown":
            console.error("Upload failed: Unknown error", error);
            break;
          default:
            console.error(`Upload failed: ${error.code}`, error);
        }
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          console.log("File available at", downloadURL);
          resolve(downloadURL);
        } catch (error) {
          console.error("Error getting download URL after upload", error);
          reject(error);
        }
      }
    );
  });
};

export const getDownloadUrl = async (ref: StorageReference): Promise<string | false> => {
  try {
    const url = await ref.getDownloadURL();
    return url;
  } catch (error) {
    const storageError = error as StorageError;
    switch (storageError.code) {
      case "storage/object-not-found":
        return false;
      case "storage/unauthorized":
        console.error("Failed to get download URL: Unauthorized access", storageError);
        break;
      case "storage/canceled":
        console.error("Failed to get download URL: Canceled", storageError);
        break;
      case "storage/unknown":
        console.error("Failed to get download URL: Unknown error", storageError);
        break;
      default:
        console.error(`Failed to get download URL: ${storageError.code}`, storageError);
    }
    throw storageError;
  }
};

export const downloadFile = async (ref: StorageReference): Promise<Blob | false> => {
  try {
    const url = await ref.getDownloadURL();
    const xhr = new XMLHttpRequest();
    xhr.responseType = "blob";
    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          console.error(`Failed to download file: HTTP ${xhr.status}`);
          reject(new Error(`HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => {
        console.error("Network error while downloading file");
        reject(new Error("Network error"));
      };
      xhr.open("GET", url);
      xhr.send();
    });
  } catch (error) {
    const storageError = error as StorageError;
    switch (storageError.code) {
      case "storage/object-not-found":
        return false;
      case "storage/unauthorized":
        console.error("Failed to download file: Unauthorized access", storageError);
        break;
      case "storage/canceled":
        console.error("Failed to download file: Canceled", storageError);
        break;
      case "storage/unknown":
        console.error("Failed to download file: Unknown error", storageError);
        break;
      default:
        console.error(`Failed to download file: ${storageError.code}`, storageError);
    }
    throw storageError;
  }
};

export const deleteFile = async (ref: StorageReference): Promise<boolean> => {
  console.log("Deleting file", ref.fullPath);
  try {
    await ref.delete();
    return true;
  } catch (error) {
    console.error("Failed to delete file", error);
    return false;
  }
};